// import { runInAction } from "mobx";
// import { observer, useLocalStore } from "mobx-react";
import { AccelFile, DisposableStack } from "@axl/membership-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSdk } from "../lib/hooks";
import { useAudioContext } from "../lib/hooks/useAudioRecorder";
import S3Uploader, {
  AbortEventArgs,
  BeginEventArgs,
  CompleteEventArgs,
  ErrorEventArgs,
  ProgressEventArgs,
  UploadFileArgs,
} from "../lib/s3-uploader";

// import { AccelFile } from "../../models";
// import { DisposableStack } from "../../utils";
// import S3Uploader, {
//   AbortEventArgs,
//   BeginEventArgs,
//   CompleteEventArgs,
//   ErrorEventArgs,
//   ProgressEventArgs,
// } from "../../utils/s3-uploader/s3-uploader";
// import { Context } from "../AccelProvider/AccelProvider";
// import { useAudioContext } from "../hooks/useAudioRecorder";

// import type { UploadFileArgs } from "../../api/methods/file";

export type FileUploaderHandler = {
  upload: () => Promise<boolean>;
  abort: () => Promise<boolean>;
};
export type FileUploaderState = {
  file: AccelFile;
  percent: number;
  status: "idle" | "uploading" | "aborted" | "completed" | "error";
};

/**
 * Upload arguments, or a function of the file being uploaded — task answers upload a video privately
 * and everything else publicly (`CommentFormEditor` `privateVideos`).
 */
export type FileUploadArgs = UploadFileArgs | ((file: AccelFile) => UploadFileArgs);

type Props = {
  file: AccelFile;
  autostart?: boolean;
  uploadArgs?: FileUploadArgs;
  /**
   * @deprecated use handlerRef instead via FileUploader.useHandler
   */
  handler?: (handler: FileUploaderHandler) => void;
  handlerRef?: React.RefObject<FileUploaderHandler | null>;
  onReady?: () => void;
  onBegin?: (args: BeginEventArgs) => void;
  onProgress?: (args: ProgressEventArgs) => void;
  onComplete?: (args: CompleteEventArgs) => void;
  onPrepare?: () => void;
  onError?: (args: ErrorEventArgs) => void;
  onAbort?: (args: AbortEventArgs) => void;
  children?: (state: FileUploaderState) => React.ReactNode;
};

export function FileUploader({
  file,
  autostart,
  uploadArgs,
  handler,
  handlerRef,
  onReady,
  onPrepare,
  onAbort,
  onBegin,
  onComplete,
  onError,
  onProgress,
  children,
}: Props) {
  const sdk = useSdk();
  const [state, updateState] = useState({
    file,
    percent: 0,
    status: file.id != null && file.nativeFile == null ? "completed" : "idle",
  } as FileUploaderState);
  const setState = useCallback((nextState: Partial<FileUploaderState>) => {
    updateState((prev) => ({ ...prev, ...nextState }));
  }, []);
  const uploader = useMemo(() => new S3Uploader(sdk.api.file), [sdk.api.file]);
  const disposableStack = useMemo(() => new DisposableStack(), []);

  const [getAudioMeta] = useAudioContext();

  useEffect(() => {
    const h: FileUploaderHandler = {
      upload: () => {
        return uploader.upload();
      },
      abort: () => {
        return uploader.abort();
      },
    };
    handler?.(h);
    if (handlerRef)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      handlerRef.current = h;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!file.nativeFile) {
      onError?.({ errors: [] });

      return;
    }

    const args = (typeof uploadArgs === "function" ? uploadArgs(file) : uploadArgs) ?? {};

    disposableStack.push(
      uploader.onBegin.on((x) => {
        file.update({ id: x.id });
        setState({
          percent: 0,
          status: "uploading",
        });
        onBegin?.(x);
      }),
    );
    disposableStack.push(
      uploader.onProgress.on((x) => {
        setState({
          percent: x.percent,
        });
        onProgress?.(x);
      }),
    );
    disposableStack.push(
      uploader.onComplete.on((x) => {
        file.update(
          {
            nativeFile: null,
            cloudKey: x.file.cloudKey,
            type: x.file.type,
            isPublic: args.isPublic ?? x.file.isPublic,
          },
          true,
        );
        setState({
          percent: 100,
          status: "completed",
        });
        onComplete?.(x);
      }),
    );
    disposableStack.push(
      uploader.onPrepare.on(() => {
        onPrepare?.();
      }),
    );
    disposableStack.push(
      uploader.onAborted.on((x) => {
        setState({
          status: "aborted",
        });
        onAbort?.(x);
      }),
    );
    disposableStack.push(
      uploader.onError.on((x) => {
        setState({
          status: "error",
        });
        onError?.(x);
      }),
    );

    (async () => {
      if (file.nativeFile) {
        args.fileId = file.id;

        if (args.isPublic == null) args.isPublic = file.isImage;

        if (file.isAudio) {
          try {
            const audioMeta = await getAudioMeta(file.nativeFile);
            args.meta = audioMeta;
            file.update({ additionalData: audioMeta });
          } catch (e) {
            // skip decoding and continue without metadata
            // console.warn(`Couldn't decode audio meta`, e);
          }
        }

        uploader.setFile(file.nativeFile, args);

        if (autostart !== false) uploader.upload();

        onReady?.();
      }
    })();

    return () => disposableStack.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!children) return null;

  return <>{children(state)}</>;
}

FileUploader.useHandler = () => {
  const ref = useRef<FileUploaderHandler>(null);

  return ref;
};
